import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Copy, Mail, Phone, Check, Loader2, MapPin, CheckCircle, AlertTriangle } from 'lucide-react';
import { deviceDisplayName } from '../../utils/deviceName';

export const SharePositionModal = ({ open, onOpenChange, device, api }) => {
  const { isNotificationChannelActive, getNotificationDisabledReason } = useFeatureFlags();
  const [tab, setTab] = useState('link');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [alert, setAlert] = useState(null); // {success, message}

  const canEmail = isNotificationChannelActive('notification_email');
  const canSMS = isNotificationChannelActive('notification_sms');
  const emailDisabledReason = getNotificationDisabledReason('notification_email');
  const smsDisabledReason = getNotificationDisabledReason('notification_sms');

  const lat = device?.last_latitude;
  const lng = device?.last_longitude;
  const name = deviceDisplayName(device);
  const address = device?.last_address || '';
  const lastSeen = device?.last_seen || '';
  const mapsLink = lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : '';

  const handleCopyLink = async () => {
    if (!mapsLink) { setAlert({ success: false, message: 'No position data available' }); return; }
    try {
      await navigator.clipboard.writeText(mapsLink);
      setCopied(true);
      setAlert({ success: true, message: 'Link copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setAlert({ success: false, message: 'Failed to copy link' });
    }
  };

  const handleSend = async () => {
    if (!recipient.trim()) {
      setAlert({ success: false, message: tab === 'email' ? 'Please enter an email address' : 'Please enter a phone number' });
      return;
    }
    if (!lat || !lng) { setAlert({ success: false, message: 'No position data available' }); return; }
    setSending(true);
    setAlert(null);
    try {
      await api.post('/share/position', {
        device_id: device.id,
        method: tab,
        recipient: recipient.trim(),
        message: message.trim(),
      });
      setAlert({ success: true, message: `Position shared via ${tab === 'email' ? 'email' : 'SMS'} to ${recipient.trim()}` });
      setRecipient('');
      setMessage('');
    } catch (err) {
      setAlert({ success: false, message: err.response?.data?.detail || `Failed to share via ${tab}` });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v) => {
    if (!v) { setAlert(null); setRecipient(''); setMessage(''); }
    onOpenChange(v);
  };

  if (!device) return null;

  const tabs = [{ id: 'link', label: 'Copy Link', icon: Copy }];
  if (canEmail) tabs.push({ id: 'email', label: 'Email', icon: Mail });
  if (canSMS) tabs.push({ id: 'sms', label: 'SMS', icon: Phone });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="share-position-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#2563eb]" />
            Share Position
          </DialogTitle>
        </DialogHeader>

        <div className="p-3 rounded-lg bg-muted/50 border text-sm space-y-1">
          <p className="font-semibold">{name}</p>
          {lat && lng && (
            <p className="text-xs text-muted-foreground">{Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}</p>
          )}
          {address && <p className="text-xs text-muted-foreground">{address}</p>}
          {lastSeen && <p className="text-xs text-muted-foreground">Last updated: {lastSeen}</p>}
          {!lat && !lng && (
            <p className="text-xs text-red-500">No position data available for this device</p>
          )}
        </div>

        <div className="flex gap-1 border-b">
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setAlert(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                tab === t.id ? 'border-b-2 border-[#2563eb] text-[#2563eb]' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`share-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {tab === 'link' && (
          <div className="flex gap-2">
            <Input value={mapsLink} readOnly className="text-xs bg-muted" data-testid="share-link-input" />
            <Button onClick={handleCopyLink} disabled={!mapsLink} className="shrink-0" data-testid="share-copy-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        )}

        {tab === 'email' && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Recipient Email</Label>
              <Input type="email" value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="email@example.com" data-testid="share-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a note..." rows={2} className="text-sm" data-testid="share-email-message" />
            </div>
            <Button onClick={handleSend} disabled={sending || !mapsLink} className="w-full" data-testid="share-email-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </div>
        )}

        {tab === 'sms' && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Recipient Phone Number</Label>
              <Input type="tel" value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="+61 400 000 000" data-testid="share-sms-input" />
            </div>
            <Button onClick={handleSend} disabled={sending || !mapsLink} className="w-full" data-testid="share-sms-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
              Send SMS
            </Button>
          </div>
        )}

        {alert && (
          <div className={`p-3 rounded-lg border text-center ${alert.success ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center justify-center gap-2">
              {alert.success ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
              <p className="text-sm font-medium">{alert.message}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
